import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import { CreateTransferDrawer } from "../components/warehouse/CreateTransferDrawer";
import { TransferDetailDrawer } from "../components/warehouse/TransferDetailDrawer";
import { InternalTransferDrawer } from "../components/warehouse/InternalTransferDrawer";
import { InternalTransferDetailDrawer } from "../components/warehouse/InternalTransferDetailDrawer";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard, Chips, Sheet, MSelect } from "../components/mobile/primitives";
import "./Items.css";
import "./Maintenance.css";

type Tab = "stock" | "transfers" | "internal";
type TStatus = "planned" | "in_transit" | "completed" | "cancelled";
type IStatus = "pending" | "approved" | "cancelled";

const STATUS_META: Record<TStatus, { cls: string; label: string; icon: string }> = {
  planned: { cls: "b-warn", label: "Planlandı", icon: "clock" },
  in_transit: { cls: "b-info", label: "Yolda", icon: "truck" },
  completed: { cls: "b-success", label: "Tamamlandı", icon: "check" },
  cancelled: { cls: "", label: "İptal", icon: "x" },
};
const IT_STATUS_META: Record<IStatus, { cls: string; label: string; icon: string }> = {
  pending: { cls: "b-warn", label: "Onay bekliyor", icon: "clock" },
  approved: { cls: "b-success", label: "Onaylandı", icon: "check" },
  cancelled: { cls: "", label: "İptal", icon: "x" },
};

function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n); }
function fmtDate(d: string | null): string { return d ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d)) : "—"; }

export function DepoYonetimiPage() {
  const isMobile = useIsMobile();
  const [filterOpen, setFilterOpen] = useState(false); // mobil transfer filtre sheet'i
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const canManage = !!meQ.data?.user && (meQ.data.user.isSuperAdmin || (meQ.data.user.permissions ?? []).includes("manage:transfer"));
  const isSuperAdmin = !!meQ.data?.user?.isSuperAdmin;
  const perms = meQ.data?.user?.permissions ?? [];
  const canCreateIT = !!meQ.data?.user && (isSuperAdmin || perms.includes("create:stock_transfer"));
  const canApproveIT = !!meQ.data?.user && (isSuperAdmin || perms.includes("approve:stock_transfer"));

  const [tab, setTab] = useState<Tab>("stock");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  // İç transfer (lojistiksiz, çift-onaylı)
  const [itCreateOpen, setItCreateOpen] = useState(false);
  const [itDetailId, setItDetailId] = useState<string | null>(null);
  const [itStatus, setItStatus] = useState<IStatus | "">("");
  const [itPage, setItPage] = useState(1);
  const itListQ = trpc.internalTransfer.list.useQuery({ page: itPage, pageSize: 25, status: (itStatus || undefined) as IStatus | undefined }, { staleTime: 5_000 });
  const itRows = itListQ.data?.rows ?? [];
  const itTotal = itListQ.data?.total ?? 0;
  const itTotalPages = Math.max(1, Math.ceil(itTotal / 25));

  // Canlı stok
  const liveQ = trpc.warehouse.liveStock.useQuery(undefined, { staleTime: 10_000 });
  const warehouses = liveQ.data?.warehouses ?? [];

  // Planlama listesi + filtreler
  const [fStatus, setFStatus] = useState<TStatus | "">("");
  const [fDay, setFDay] = useState("");
  const [fVehicle, setFVehicle] = useState("");
  const [fDriver, setFDriver] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const listInput = useMemo(() => ({
    page, pageSize,
    status: (fStatus || undefined) as TStatus | undefined,
    day: fDay || undefined,
    vehiclePlate: fVehicle || undefined,
    driverName: fDriver || undefined,
  }), [page, fStatus, fDay, fVehicle, fDriver]);
  const listQ = trpc.warehouse.list.useQuery(listInput, { staleTime: 5_000 });
  const optsQ = trpc.warehouse.filterOptions.useQuery(undefined, { staleTime: 30_000 });
  const plannedQ = trpc.warehouse.list.useQuery({ status: "planned", pageSize: 1 }, { staleTime: 10_000 });
  const transitQ = trpc.warehouse.list.useQuery({ status: "in_transit", pageSize: 1 }, { staleTime: 10_000 });

  const rows = listQ.data?.rows ?? [];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const totalItems = warehouses.reduce((s, w) => s + w.itemCount, 0);
  const totalLots = warehouses.reduce((s, w) => s + w.lotCount, 0);

  const kpi = (label: string, val: string | number, sub: string, color: string) => (
    <div className="dash-kpi" style={{ ["--kpi-color" as string]: color }}>
      <div className="head"><span className="lbl">{label}</span></div>
      <div className="val">{val}</div>
      <div className="sub">{sub}</div>
    </div>
  );

  // ═══ Aksiyon drawer'ları — masaüstü + mobil ORTAK (Drawer ≤767px'te tam-ekran) ═══
  const drawers = (
    <>
      <CreateTransferDrawer open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => setDetailId(id)} />
      <TransferDetailDrawer open={!!detailId} transferId={detailId} canManage={canManage} isSuperAdmin={isSuperAdmin} onClose={() => setDetailId(null)} />
      <InternalTransferDrawer open={itCreateOpen} onClose={() => setItCreateOpen(false)} onCreated={(id) => setItDetailId(id)} />
      <InternalTransferDetailDrawer open={!!itDetailId} transferId={itDetailId} canCreate={canCreateIT} canApprove={canApproveIT} isSuperAdmin={isSuperAdmin} onClose={() => setItDetailId(null)} />
    </>
  );

  // ═══ MOBİL GÖRÜNÜM (≤767px) — KPI + sekme chips + kart listeleri ═══
  if (isMobile) {
    const filterCount = [fDay, fVehicle, fDriver].filter(Boolean).length;
    const pagerRow = (pg: number, tp: number, set: (u: (p: number) => number) => void, label: string, count: number) => (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
        <span className="m-ovl">{count} {label}</span>
        <span style={{ flex: 1 }} />
        {tp > 1 ? (
          <>
            <button type="button" className="btn-md secondary sm" disabled={pg === 1} onClick={() => set((n) => Math.max(1, n - 1))}>‹</button>
            <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{pg} / {tp}</span>
            <button type="button" className="btn-md secondary sm" disabled={pg >= tp} onClick={() => set((n) => Math.min(tp, n + 1))}>›</button>
          </>
        ) : null}
      </div>
    );
    return (
      <>
        <MCard pad>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {([
              ["Depo", liveQ.data ? String(warehouses.length) : "…"],
              ["Kalem", liveQ.data ? String(totalItems) : "…"],
              ["Lot", liveQ.data ? String(totalLots) : "…"],
              ["Planlı", plannedQ.data ? String(plannedQ.data.total) : "…"],
              ["Yolda", transitQ.data ? String(transitQ.data.total) : "…"],
            ] as Array<[string, string]>).map(([l, v]) => (
              <div key={l}>
                <div className="m-ovl">{l}</div>
                <div className="mono" style={{ fontSize: 18, fontWeight: 700, color: "var(--neutral-900)", marginTop: 2 }}>{v}</div>
              </div>
            ))}
          </div>
        </MCard>

        <div className="m-stickytop">
          <Chips>
            <button type="button" className={`m-chip ${tab === "stock" ? "act" : ""}`} onClick={() => setTab("stock")}>Canlı Stok{liveQ.data ? ` (${warehouses.length})` : ""}</button>
            <button type="button" className={`m-chip ${tab === "transfers" ? "act" : ""}`} onClick={() => setTab("transfers")}>Transfer{listQ.data ? ` (${total})` : ""}</button>
            <button type="button" className={`m-chip ${tab === "internal" ? "act" : ""}`} onClick={() => setTab("internal")}>İç Transfer{itListQ.data ? ` (${itTotal})` : ""}</button>
          </Chips>
        </div>

        {tab !== "internal" && canManage ? (
          <button type="button" className="btn-md primary sm" style={{ alignSelf: "flex-start" }} onClick={() => setCreateOpen(true)}><Icon name="plus" size={14} />Transfer Planı</button>
        ) : null}
        {tab === "internal" && canCreateIT ? (
          <button type="button" className="btn-md primary sm" style={{ alignSelf: "flex-start" }} onClick={() => setItCreateOpen(true)}><Icon name="plus" size={14} />İç Transfer</button>
        ) : null}

        {tab === "stock" ? (
          liveQ.isLoading ? <div className="m-empty">Yükleniyor…</div>
          : warehouses.length === 0 ? <div className="m-empty">Transfer edilir depo yok — Lokasyonlar'da bir depoyu "Transfer edilir" işaretleyin.</div>
          : warehouses.map((w) => (
            <MCard className="m-list" key={w.id}>
              <div className="m-li" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" }}>
                <span className="m-ric" style={{ background: "var(--info-subtle)", color: "var(--info)" }}><Icon name="warehouse" size={17} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700 }}>{w.name}</span>
                  <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{w.code} · {w.itemCount} kalem · {w.lotCount} lot</span>
                </span>
              </div>
              {w.items.length === 0 ? (
                <div className="m-li" style={{ padding: "10px 14px", fontSize: 12.5, color: "var(--neutral-500)" }}>Bu depoda stok yok.</div>
              ) : w.items.map((it) => (
                <div className="m-li" key={`${it.itemKind}-${it.itemId}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px" }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 600 }}>{it.name}</span>
                    <span className="mono" style={{ display: "block", fontSize: 10.5, color: "var(--neutral-400)" }}>{it.code}</span>
                  </span>
                  <span className="mono" style={{ fontSize: 12.5, fontWeight: 700 }}>{fmtQty(it.onHand)} <small style={{ color: "var(--neutral-400)", fontWeight: 600 }}>{it.uom}</small></span>
                  <span className="badge b-neutral no-dot">{it.lotCount} lot</span>
                </div>
              ))}
            </MCard>
          ))
        ) : tab === "transfers" ? (
          <>
            <Chips>
              <button type="button" className={`m-chip ${fStatus === "" ? "act" : ""}`} onClick={() => { setFStatus(""); setPage(1); }}>Tümü</button>
              {(Object.entries(STATUS_META) as Array<[TStatus, { cls: string; label: string; icon: string }]>).map(([v, m]) => (
                <button type="button" key={v} className={`m-chip ${fStatus === v ? "act" : ""}`} onClick={() => { setFStatus(v); setPage(1); }}>{m.label}</button>
              ))}
            </Chips>
            <div style={{ display: "flex" }}>
              <button type="button" className={`btn-md sm ${filterCount ? "primary" : "secondary"}`} onClick={() => setFilterOpen(true)}><Icon name="list2" size={14} />Filtrele{filterCount ? ` (${filterCount})` : ""}</button>
            </div>
            {listQ.isLoading ? <div className="m-empty">Yükleniyor…</div>
            : rows.length === 0 ? <div className="m-empty">{filterCount || fStatus ? "Filtreye uygun transfer yok." : "Transfer yok — \"Transfer Planı\" ile başlatın."}</div>
            : rows.map((t) => {
              const m = STATUS_META[t.status as TStatus] ?? { cls: "b-neutral", label: t.status, icon: "clock" };
              return (
                <MCard pad key={t.id} onClick={() => setDetailId(t.id)} style={{ cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: "var(--neutral-900)" }}>{t.code}</span>
                    <span className={`badge ${m.cls === "b-warn" ? "b-warning" : m.cls || "b-neutral"} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginTop: 8 }}>
                    {t.fromName} <Icon name="arrowRight" size={13} /> {t.toName}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <div style={{ flex: 1.3 }}><div className="m-ovl">Planlanan</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{fmtDate(t.plannedDate)}{t.plannedTime ? ` ${t.plannedTime}` : ""}</div></div>
                    <div style={{ flex: 1 }}><div className="m-ovl">Araç</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{t.vehiclePlate ?? "—"}</div></div>
                    <div style={{ flex: 1 }}><div className="m-ovl">Şoför</div><div style={{ fontSize: 12, fontWeight: 500, marginTop: 2, color: t.driverName ? "var(--neutral-800)" : "var(--neutral-400)" }}>{t.driverName ?? "—"}</div></div>
                  </div>
                </MCard>
              );
            })}
            {pagerRow(page, totalPages, setPage, "transfer", total)}
          </>
        ) : (
          <>
            <Chips>
              <button type="button" className={`m-chip ${itStatus === "" ? "act" : ""}`} onClick={() => { setItStatus(""); setItPage(1); }}>Tümü</button>
              {(Object.entries(IT_STATUS_META) as Array<[IStatus, { cls: string; label: string; icon: string }]>).map(([v, m]) => (
                <button type="button" key={v} className={`m-chip ${itStatus === v ? "act" : ""}`} onClick={() => { setItStatus(v); setItPage(1); }}>{m.label}</button>
              ))}
            </Chips>
            {itListQ.isLoading ? <div className="m-empty">Yükleniyor…</div>
            : itRows.length === 0 ? <div className="m-empty">{itStatus ? "Filtreye uygun kayıt yok." : "İç transfer yok — \"İç Transfer\" ile başlatın."}</div>
            : itRows.map((r) => {
              const m = IT_STATUS_META[r.status as IStatus] ?? { cls: "b-neutral", label: r.status, icon: "clock" };
              return (
                <MCard pad key={r.id} onClick={() => setItDetailId(r.id)} style={{ cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: "var(--neutral-900)" }}>{r.code}</span>
                    <span className={`badge ${m.cls === "b-warn" ? "b-warning" : m.cls || "b-neutral"} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginTop: 8 }}>
                    {r.fromName} <Icon name="arrowRight" size={13} /> {r.toName}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <div style={{ flex: 1.4 }}><div className="m-ovl">Talep Eden</div><div style={{ fontSize: 12, fontWeight: 500, marginTop: 2 }}>{r.requestedBy}</div></div>
                    <div style={{ flex: 1 }}><div className="m-ovl">Tarih</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{fmtDate(r.requestedAt as unknown as string)}</div></div>
                    <div style={{ flex: 0.7 }}><div className="m-ovl">Satır</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.lineCount}</div></div>
                  </div>
                </MCard>
              );
            })}
            {pagerRow(itPage, itTotalPages, setItPage, "iç transfer", itTotal)}
          </>
        )}

        <Sheet open={filterOpen} title="Transfer Filtresi" onClose={() => setFilterOpen(false)}
          footer={<>
            <button type="button" className="btn-md secondary" onClick={() => { setFDay(""); setFVehicle(""); setFDriver(""); setPage(1); }}>Temizle</button>
            <button type="button" className="btn-md primary" onClick={() => setFilterOpen(false)}>Uygula</button>
          </>}>
          <div className="field">
            <label className="field-label">Gün</label>
            <input className="m-input" style={{ fontFamily: "var(--font-sans)" }} type="date" value={fDay} onChange={(e) => { setFDay(e.target.value); setPage(1); }} />
          </div>
          <MSelect label="Araç" value={fVehicle} placeholder="Tümü" options={optsQ.data?.vehicles ?? []} onChange={(v) => { setFVehicle(v); setPage(1); }} />
          <MSelect label="Şoför" value={fDriver} placeholder="Tümü" options={optsQ.data?.drivers ?? []} onChange={(v) => { setFDriver(v); setPage(1); }} />
        </Sheet>

        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Depo Yönetimi</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Depo Yönetimi</h1>
          <p className="sub">Depolar-arası transfer planlama · araç/şoför lojistiği · kantar · canlı depo stoğu</p>
        </div>
        {canManage ? (
          <button type="button" className="btn-md primary" onClick={() => setCreateOpen(true)}><Icon name="plus" size={14} />Transfer Planı</button>
        ) : null}
      </div>

      <div className="dash-kpi-grid mt-kpis" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
        {kpi("TRANSFER DEPOSU", liveQ.data ? warehouses.length : "…", "canlı stok", "var(--info)")}
        {kpi("TOPLAM KALEM", liveQ.data ? totalItems : "…", "tüm depolar", "var(--primary)")}
        {kpi("TOPLAM LOT", liveQ.data ? totalLots : "…", "stok satırı", "var(--neutral-500)")}
        {kpi("PLANLI TRANSFER", plannedQ.data ? plannedQ.data.total : "…", "bekliyor", "var(--warning)")}
        {kpi("YOLDA", transitQ.data ? transitQ.data.total : "…", "taşınıyor", "var(--info)")}
      </div>

      <div className="dt2-wrap">
        <div className="dt2-tabs">
          <button type="button" className={`dt2-tab ${tab === "stock" ? "active" : ""}`} onClick={() => setTab("stock")}>Canlı Depo Stoğu{liveQ.data ? <span className="ct">{warehouses.length}</span> : null}</button>
          <button type="button" className={`dt2-tab ${tab === "transfers" ? "active" : ""}`} onClick={() => setTab("transfers")}>Transfer Planlama{listQ.data ? <span className="ct">{total}</span> : null}</button>
          <button type="button" className={`dt2-tab ${tab === "internal" ? "active" : ""}`} onClick={() => setTab("internal")}>İç Transferler{itListQ.data ? <span className="ct">{itTotal}</span> : null}</button>
        </div>

        {/* ── SEKME: Canlı Depo Stoğu ── */}
        {tab === "stock" ? (
          <div className="dt2-scroll" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 14 }}>
            {liveQ.isLoading ? <div className="mt-empty">Yükleniyor…</div> : warehouses.length === 0 ? (
              <div className="empty-state"><p className="ttl">Transfer edilir depo yok</p><p className="sub">Lokasyonlar'da bir depoyu "Transfer edilir" işaretleyin; canlı stoğu ve transferleri burada yönetin.</p></div>
            ) : warehouses.map((w) => (
              <div className="d-card" key={w.id}>
                <div className="d-card-head">
                  <div className="left"><span className="t"><Icon name="warehouse" size={14} /> {w.name}</span><span className="s">{w.code} · {w.itemCount} kalem · {w.lotCount} lot</span></div>
                </div>
                <div className="d-card-body flush">
                  {w.items.length === 0 ? (
                    <div style={{ padding: "12px 14px", fontSize: 12.5, color: "var(--neutral-500)" }}>Bu depoda stok yok.</div>
                  ) : (
                    <table className="dt2">
                      <thead><tr><th>Ürün</th><th style={{ width: 130 }}>Kod</th><th className="num" style={{ width: 140 }}>Eldeki</th><th className="num" style={{ width: 70 }}>Lot</th></tr></thead>
                      <tbody>
                        {w.items.map((it) => (
                          <tr key={`${it.itemKind}-${it.itemId}`}>
                            <td>{it.name}</td>
                            <td className="cell-id" style={{ fontSize: 11 }}>{it.code}</td>
                            <td className="cell-num" style={{ fontWeight: 600 }}>{fmtQty(it.onHand)} <span style={{ fontSize: 10, color: "var(--neutral-400)" }}>{it.uom}</span></td>
                            <td className="cell-num">{it.lotCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* ── SEKME: Transfer Planlama ── */}
        {tab === "transfers" ? (
          <>
            <div className="mt-filters">
              <div className="mt-filter">
                <span className="mt-filter-label">Gün</span>
                <input className="form-input" type="date" value={fDay} onChange={(e) => { setFDay(e.target.value); setPage(1); }} />
              </div>
              <div className="mt-filter">
                <span className="mt-filter-label">Araç</span>
                <select className="form-select" value={fVehicle} onChange={(e) => { setFVehicle(e.target.value); setPage(1); }}>
                  <option value="">Tümü</option>
                  {(optsQ.data?.vehicles ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="mt-filter">
                <span className="mt-filter-label">Şoför</span>
                <select className="form-select" value={fDriver} onChange={(e) => { setFDriver(e.target.value); setPage(1); }}>
                  <option value="">Tümü</option>
                  {(optsQ.data?.drivers ?? []).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="mt-filter">
                <span className="mt-filter-label">Durum</span>
                <select className="form-select" value={fStatus} onChange={(e) => { setFStatus(e.target.value as TStatus | ""); setPage(1); }}>
                  <option value="">Tümü</option>
                  {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
                </select>
              </div>
              {(fDay || fVehicle || fDriver || fStatus) ? (
                <button type="button" className="btn-md ghost" style={{ alignSelf: "flex-end" }} onClick={() => { setFDay(""); setFVehicle(""); setFDriver(""); setFStatus(""); setPage(1); }}>Filtreleri temizle</button>
              ) : null}
            </div>
            <div className="dt2-scroll">
              {listQ.isLoading ? <div className="mt-empty">Yükleniyor…</div> : rows.length === 0 ? (
                <div className="empty-state"><p className="ttl">Transfer yok</p><p className="sub">{fDay || fVehicle || fDriver || fStatus ? "Filtreye uygun transfer yok." : "\"Transfer Planı\" ile depolar-arası transfer başlatın."}</p></div>
              ) : (
                <table className="dt2">
                  <thead><tr><th style={{ width: 150 }}>Kod</th><th>Güzergah</th><th style={{ width: 130 }}>Planlanan</th><th style={{ width: 120 }}>Araç</th><th>Şoför</th><th className="num" style={{ width: 60 }}>Lot</th><th style={{ width: 130 }}>Durum</th></tr></thead>
                  <tbody>
                    {rows.map((t) => {
                      const m = STATUS_META[t.status as TStatus];
                      return (
                        <tr key={t.id} onClick={() => setDetailId(t.id)} style={{ cursor: "pointer" }} title="Transfer detayını aç">
                          <td className="cell-id">{t.code}</td>
                          <td>{t.fromName} <Icon name="arrowRight" size={12} /> {t.toName}</td>
                          <td style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{fmtDate(t.plannedDate)}{t.plannedTime ? ` ${t.plannedTime}` : ""}</td>
                          <td style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{t.vehiclePlate ?? "—"}</td>
                          <td>{t.driverName ?? <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                          <td className="cell-num">{t.lineCount > 0 ? t.lineCount : "—"}</td>
                          <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="dt2-foot">
              <span>{total} transfer</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <button type="button" className="pg" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Önceki">‹</button>
                <span className="mono">{page}</span>
                <span style={{ color: "var(--neutral-400)", fontSize: 12 }}>/ {totalPages}</span>
                <button type="button" className="pg" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Sonraki">›</button>
              </span>
            </div>
          </>
        ) : null}

        {/* ── SEKME: İç Transferler (lojistiksiz, çift-onaylı) ── */}
        {tab === "internal" ? (
          <>
            <div className="mt-filters">
              <div className="mt-filter">
                <span className="mt-filter-label">Durum</span>
                <select className="form-select" value={itStatus} onChange={(e) => { setItStatus(e.target.value as IStatus | ""); setItPage(1); }}>
                  <option value="">Tümü</option>
                  {Object.entries(IT_STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
                </select>
              </div>
              {itStatus ? (
                <button type="button" className="btn-md ghost" style={{ alignSelf: "flex-end" }} onClick={() => { setItStatus(""); setItPage(1); }}>Temizle</button>
              ) : null}
              {canCreateIT ? (
                <button type="button" className="btn-md primary" style={{ alignSelf: "flex-end", marginLeft: "auto" }} onClick={() => setItCreateOpen(true)}><Icon name="plus" size={14} />İç Transfer</button>
              ) : null}
            </div>
            <div className="dt2-scroll">
              {itListQ.isLoading ? <div className="mt-empty">Yükleniyor…</div> : itRows.length === 0 ? (
                <div className="empty-state"><p className="ttl">İç transfer yok</p><p className="sub">{itStatus ? "Filtreye uygun kayıt yok." : "\"İç Transfer\" ile depolar-arası (lojistiksiz) stok geçişi başlatın — onaylandığında stok taşınır."}</p></div>
              ) : (
                <table className="dt2">
                  <thead><tr><th style={{ width: 150 }}>Kod</th><th>Güzergah</th><th>Talep Eden</th><th style={{ width: 150 }}>Talep Tarihi</th><th className="num" style={{ width: 60 }}>Satır</th><th style={{ width: 140 }}>Durum</th></tr></thead>
                  <tbody>
                    {itRows.map((r) => {
                      const m = IT_STATUS_META[r.status as IStatus];
                      return (
                        <tr key={r.id} onClick={() => setItDetailId(r.id)} style={{ cursor: "pointer" }} title="İç transfer detayını aç">
                          <td className="cell-id">{r.code}</td>
                          <td>{r.fromName} <Icon name="arrowRight" size={12} /> {r.toName}</td>
                          <td>{r.requestedBy}</td>
                          <td style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{fmtDate(r.requestedAt as unknown as string)}</td>
                          <td className="cell-num">{r.lineCount}</td>
                          <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="dt2-foot">
              <span>{itTotal} iç transfer</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <button type="button" className="pg" onClick={() => setItPage((p) => Math.max(1, p - 1))} disabled={itPage <= 1} aria-label="Önceki">‹</button>
                <span className="mono">{itPage}</span>
                <span style={{ color: "var(--neutral-400)", fontSize: 12 }}>/ {itTotalPages}</span>
                <button type="button" className="pg" onClick={() => setItPage((p) => Math.min(itTotalPages, p + 1))} disabled={itPage >= itTotalPages} aria-label="Sonraki">›</button>
              </span>
            </div>
          </>
        ) : null}
      </div>

      {drawers}
    </div>
  );
}
